import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/owlCollectingPage.css";
import { API_BASE } from "../config";
import gameConfig from "../data/gameConfig.json";
import apiFetch from "../apiFetch";

const GACHA_CFG = gameConfig.owlGacha;
const RARITY_ORDER = ["Common", "Uncommon", "Rare", "Epic", "Legendary", "Mythical"];
const BG_STYLE = {
  backgroundImage: `url(${process.env.PUBLIC_URL}/images/owlNestImg.png)`,
};

const getOwlImage = (owl) => owl?.image_url ? `${API_BASE}${owl.image_url}` : `${process.env.PUBLIC_URL}/images/owlTestImgNoBG.png`;

const RARITY_GLOW = {
  Common:    "rgba(180,120,20,0.7)",
  Uncommon:  "rgba(16,185,129,0.75)",
  Rare:      "rgba(65,105,225,0.8)",
  Epic:      "rgba(148,0,211,0.85)",
  Legendary: "rgba(255,215,0,0.95)",
  Mythical:  "rgba(255,20,147,0.95)",
};

const getRollCost = (count) => {
  const base = GACHA_CFG.singleRollCost * count;
  if (count === 100) return Math.floor(base * (1 - GACHA_CFG.x100Discount));
  if (count === 10) return Math.floor(base * (1 - GACHA_CFG.x10Discount));
  return base;
};

// ── Main component ────────────────────────────────────────────────────────────

const OwlLotterySystem = () => {
  const navigate = useNavigate();
  const [coins, setCoins] = useState(null);
  const [pirateOwls, setPirateOwls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rollError, setRollError] = useState("");

  // "home" | "rolling" | "reveal"
  const [screen, setScreen] = useState("home");
  const [rollPhase, setRollPhase] = useState(0); // 0=shake 1=glow 2=flash
  const [rolledOwls, setRolledOwls] = useState([]);
  const [carouselIdx, setCarouselIdx] = useState(0);
  // decided[i] = "collected" | "discarded"
  const [decided, setDecided] = useState({});
  const [isCollectingAll, setIsCollectingAll] = useState(false);

  const timersRef = useRef([]);

  useEffect(() => { document.title = "Owl Hatchery — Owllet Reading Quest"; }, []);

  useEffect(() => {
    fetchOwls();
    fetchUserCoins();
    return () => timersRef.current.forEach(clearTimeout);
  }, []);

  const fetchOwls = async () => {
    try {
      const res = await apiFetch(`/api/owls`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPirateOwls(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
      setPirateOwls([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCoins = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    try {
      const res = await apiFetch(`/api/resources/${userId}`);
      const data = await res.json();
      setCoins(data.coins);
    } catch {
      setCoins(0);
    }
  };

  const rollOne = () => {
    if (!pirateOwls.length) return null;
    const rand = Math.random() * 100;
    let cumul = 0;
    for (const rarity of RARITY_ORDER) {
      cumul += GACHA_CFG.rarityChances[rarity];
      if (rand <= cumul) {
        const pool = pirateOwls.filter((o) => o.rarity === rarity);
        if (pool.length) return pool[Math.floor(Math.random() * pool.length)];
      }
    }
    return pirateOwls[0];
  };

  const startRoll = async (count) => {
    const cost = getRollCost(count);
    if (coins === null || coins < cost || !pirateOwls.length) return;
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    setRollError("");
    try {
      const res = await apiFetch(`/api/resources/update-coins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: parseInt(userId), delta: -cost }),
      });
      if (res.status === 402) {
        // Balance was insufficient on the server (e.g. race from two tabs / double-tap)
        const data = await res.json().catch(() => ({}));
        setRollError(data.error || "Not enough coins to roll.");
        // Re-sync the displayed balance from the server so the UI reflects reality
        apiFetch(`/api/resources/${parseInt(userId)}`)
          .then(r => r.json())
          .then(d => { if (typeof d.coins === "number") setCoins(d.coins); })
          .catch(() => {});
        return;
      }
      if (!res.ok) return;
    } catch {
      return;
    }

    setCoins((prev) => prev - cost);
    const results = Array.from({ length: count }, () => rollOne()).filter(
      Boolean,
    );
    setRolledOwls(results);
    setCarouselIdx(0);
    setDecided({});
    setRollPhase(0);
    setScreen("rolling");

    timersRef.current.forEach(clearTimeout);
    const t1 = setTimeout(() => setRollPhase(1), 1200);
    const t2 = setTimeout(() => setRollPhase(2), 2500);
    const t3 = setTimeout(() => setScreen("reveal"), 3100);
    timersRef.current = [t1, t2, t3];
  };

  const doCollect = async (idx) => {
    const owl = rolledOwls[idx];
    if (!owl || decided[idx]) return;
    const userId = localStorage.getItem("userId");
    const username = localStorage.getItem("username");
    if (!userId || !username) return;
    try {
      await apiFetch(`/api/collection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          owlId: owl.id,
          userId: parseInt(userId),
          username,
        }),
      });
    } catch {}
    setDecided((prev) => ({ ...prev, [idx]: "collected" }));
  };

  const doDiscard = async (idx) => {
    const owl = rolledOwls[idx];
    if (!owl || decided[idx]) return;
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    try {
      await apiFetch(`/api/resources/update-coins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: parseInt(userId),
          delta: owl.discard_value,
        }),
      });
      setCoins((prev) => prev + owl.discard_value);
    } catch {}
    setDecided((prev) => ({ ...prev, [idx]: "discarded" }));
  };

  const doCollectAll = async () => {
    if (isCollectingAll) return;
    const userId = localStorage.getItem("userId");
    const username = localStorage.getItem("username");
    if (!userId || !username) return;

    const undecided = rolledOwls.map((_, i) => i).filter((i) => !decided[i]);
    setIsCollectingAll(true);

    const results = await Promise.allSettled(
      undecided.map((i) =>
        apiFetch(`/api/collection`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ owlId: rolledOwls[i].id, userId: parseInt(userId), username }),
        })
      )
    );

    const newDecided = { ...decided };
    results.forEach((result, j) => {
      if (result.status === "fulfilled") newDecided[undecided[j]] = "collected";
    });
    setDecided(newDecided);
    setIsCollectingAll(false);
  };

  const goHome = () => {
    setScreen("home");
    setRolledOwls([]);
    setRollPhase(0);
    setDecided({});
    setIsCollectingAll(false);
  };

  const allDecided =
    rolledOwls.length > 0 && rolledOwls.every((_, i) => decided[i]);

  // Owls to display in the showcase panel (ordered by showcaseOwlIds config)
  const showcaseOwls = useMemo(
    () => (GACHA_CFG.showcaseOwlIds || [])
      .map((id) => pirateOwls.find((o) => o.id === id))
      .filter(Boolean),
    [pirateOwls]
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="gacha-loading-screen">
        <div className="gacha-loading-egg">🥚</div>
        <p>Loading owl data...</p>
      </div>
    );
  }

  // ── Rolling animation ──────────────────────────────────────────────────────
  if (screen === "rolling") {
    return (
      <div className={`gacha-stage gacha-rolling phase-${rollPhase}`}>
        <div className="gacha-bg" style={BG_STYLE} />
        <div className="gacha-roll-stage">
          {rollPhase >= 1 && <div className="gacha-light-rays" />}
          {rollPhase >= 1 && <div className="gacha-glow-burst" />}
          <div className={`gacha-hatching-egg phase-${rollPhase}`}>🥚</div>
        </div>
        {rollPhase === 2 && <div className="gacha-flash-overlay" />}
      </div>
    );
  }

  // ── Reveal ─────────────────────────────────────────────────────────────────
  if (screen === "reveal") {
    const isSingle = rolledOwls.length === 1;

    if (isSingle) {
      const owl = rolledOwls[0];
      const d = decided[0];
      return (
        <div className="gacha-stage gacha-reveal-screen">
          <div className="gacha-bg gacha-reveal-bg-dim" style={BG_STYLE} />
          <div className="gacha-reveal-inner">
            <OwlCard
              owl={owl}
              decided={d}
              onCollect={() => doCollect(0)}
              onDiscard={() => doDiscard(0)}
            />
            {d && (
              <div className="gacha-post-actions">
                <button className="gacha-btn-outline" onClick={goHome}>
                  Roll Again
                </button>
                <button
                  className="gacha-btn-outline"
                  onClick={() => navigate("/owlNest")}
                >
                  View Collection
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Multi-roll carousel
    const owl = rolledOwls[carouselIdx];
    const d = decided[carouselIdx];
    return (
      <div className="gacha-stage gacha-reveal-screen">
        <div className="gacha-bg gacha-reveal-bg-dim" style={BG_STYLE} />
        <div className="gacha-carousel-wrapper">
          <div className="gacha-carousel-header">
            <span className="gacha-carousel-label">
              {carouselIdx + 1} / {rolledOwls.length}
            </span>
            <div className="gacha-carousel-dots">
              {rolledOwls.map((o, i) => (
                <button
                  key={i}
                  className={[
                    "gacha-dot",
                    `gacha-dot-${o.rarity.toLowerCase()}`,
                    i === carouselIdx ? "gacha-dot-active" : "",
                    decided[i] === "collected" ? "gacha-dot-collected" : "",
                    decided[i] === "discarded" ? "gacha-dot-discarded" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => setCarouselIdx(i)}
                />
              ))}
            </div>
          </div>

          <div className="gacha-carousel-stage">
            <button
              className="gacha-carousel-arrow"
              onClick={() => setCarouselIdx((i) => Math.max(0, i - 1))}
              disabled={carouselIdx === 0}
            >
              ‹
            </button>

            <div className="gacha-carousel-card-wrap" key={carouselIdx}>
              <OwlCard
                owl={owl}
                decided={d}
                onCollect={() => doCollect(carouselIdx)}
                onDiscard={() => doDiscard(carouselIdx)}
                compact
              />
            </div>

            <button
              className="gacha-carousel-arrow"
              onClick={() =>
                setCarouselIdx((i) => Math.min(rolledOwls.length - 1, i + 1))
              }
              disabled={carouselIdx === rolledOwls.length - 1}
            >
              ›
            </button>
          </div>

          <div className="gacha-carousel-footer">
            {!allDecided && (
              <button
                className="gacha-btn-collect-all"
                onClick={doCollectAll}
                disabled={isCollectingAll}
              >
                {isCollectingAll ? "Collecting…" : "✨ Collect All Remaining"}
              </button>
            )}
            {allDecided && (
              <div className="gacha-post-actions">
                <button className="gacha-btn-outline" onClick={goHome}>
                  Roll Again
                </button>
                <button
                  className="gacha-btn-outline"
                  onClick={() => navigate("/owlNest")}
                >
                  View Collection
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Home screen ───────────────────────────────────────────────────────────
  const cost1 = getRollCost(1);
  const cost10 = getRollCost(10);
  const cost100 = getRollCost(100);

  return (
    <div className="gacha-home">
      <div className="gacha-bg gacha-home-overlay" style={BG_STYLE} />

      <div className="gacha-home-header">
        <button className="gacha-back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1 className="gacha-home-title">🦉 Owl Hatchery</h1>
        <div className="gacha-header-right">
          <button className="gacha-back-btn" onClick={() => navigate("/owlNest")}>
            🪺 My Collection
          </button>
          <div className="gacha-coins-badge">🪙 {coins ?? "..."}</div>
        </div>
      </div>

      {error && <div className="gacha-error-banner">⚠️ {error}</div>}
      {rollError && <div className="gacha-error-banner gacha-error-banner--roll">🪙 {rollError}</div>}

      <div className="gacha-home-body">

        {/* ── Left: Showcase ── */}
        <ShowcasePanel owls={showcaseOwls} />

        {/* ── Centre: Roll cards ── */}
        <div className="gacha-roll-cards">
          <RollCard
            label="Single Roll"
            count={1}
            cost={cost1}
            canAfford={coins >= cost1}
            hasOwls={pirateOwls.length > 0}
            onRoll={() => startRoll(1)}
          />
          <RollCard
            label="x10 Bundle"
            count={10}
            cost={cost10}
            originalCost={GACHA_CFG.singleRollCost * 10}
            discount="25% OFF"
            canAfford={coins >= cost10}
            hasOwls={pirateOwls.length > 0}
            featured
            onRoll={() => startRoll(10)}
          />
          <RollCard
            label="x100 Bundle"
            count={100}
            cost={cost100}
            originalCost={GACHA_CFG.singleRollCost * 100}
            discount="50% OFF"
            canAfford={coins >= cost100}
            hasOwls={pirateOwls.length > 0}
            premium
            onRoll={() => startRoll(100)}
          />
        </div>

        {/* ── Right: Rarity guide ── */}
        <div className="gacha-rarity-guide">
          <h3 className="gacha-rarity-title">Rarity Chances</h3>
          <div className="gacha-rarity-rows">
            {RARITY_ORDER.map((rarity) => (
              <div
                key={rarity}
                className={`gacha-rarity-row gacha-rarity-${rarity.toLowerCase()}`}
              >
                <span className="gacha-rarity-name">{rarity}</span>
                <div className="gacha-rarity-bar">
                  <div
                    className="gacha-rarity-bar-fill"
                    style={{
                      width: `${Math.min(100, GACHA_CFG.rarityChances[rarity] * 2)}%`,
                    }}
                  />
                </div>
                <span className="gacha-rarity-pct">
                  {GACHA_CFG.rarityChances[rarity]}%
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ShowcaseOwl = ({ owl, size, showName }) => {
  const glowColor = RARITY_GLOW[owl.rarity] || RARITY_GLOW.Legendary;
  return (
    <div className={`gacha-showcase-owl gacha-showcase-owl--${size}`}>
      <div className="gacha-showcase-halo" style={{ "--glow": glowColor }} />
      <img
        src={getOwlImage(owl)}
        alt={owl.name}
        className="gacha-showcase-img"
        style={{
          filter: `brightness(0) drop-shadow(0 0 14px ${glowColor}) drop-shadow(0 0 30px ${glowColor})`,
        }}
      />
      {showName && <p className="gacha-showcase-name">{owl.name}</p>}
    </div>
  );
};

const ShowcasePanel = ({ owls }) => {
  const [idx, setIdx] = useState(0);
  const intervalMs = (GACHA_CFG.showcaseRotationSeconds || 3) * 1000;

  useEffect(() => {
    if (owls.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % owls.length), intervalMs);
    return () => clearInterval(id);
  }, [owls.length, intervalMs]);

  if (!owls.length) return (
    <div className="gacha-showcase">
      <p className="gacha-showcase-title">✨ Featured Owls</p>
      <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "12px", textAlign: "center" }}>
        Configure showcase owl IDs in gameConfig
      </p>
    </div>
  );

  const total    = owls.length;
  const prevOwl  = owls[(idx - 1 + total) % total];
  const centerOwl = owls[idx];
  const nextOwl  = owls[(idx + 1) % total];

  return (
    <div className="gacha-showcase">
      <p className="gacha-showcase-title">✨ Featured Owls</p>

      <div className="gacha-showcase-stage">
        <div className="gacha-showcase-slot gacha-showcase-slot--side">
          <ShowcaseOwl owl={prevOwl} size="small" />
        </div>
        <div key={`center-${idx}`} className="gacha-showcase-slot gacha-showcase-slot--center">
          <ShowcaseOwl owl={centerOwl} size="large" showName />
        </div>
        <div className="gacha-showcase-slot gacha-showcase-slot--side">
          <ShowcaseOwl owl={nextOwl} size="small" />
        </div>
      </div>

      <div className="gacha-showcase-dots">
        {owls.map((_, i) => (
          <button
            key={i}
            className={`gacha-showcase-dot${i === idx ? " active" : ""}`}
            onClick={() => setIdx(i)}
            aria-label={`Show owl ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const RollCard = ({
  label,
  count,
  cost,
  originalCost,
  discount,
  canAfford,
  hasOwls,
  featured,
  premium,
  onRoll,
}) => {
  const canRoll = canAfford && hasOwls;
  return (
    <div
      className={`gacha-roll-card ${featured ? "featured" : ""} ${premium ? "premium" : ""}`}
    >
      {discount && (
        <div className={`gacha-discount-tag ${premium ? "premium" : ""}`}>
          {discount}
        </div>
      )}
      <div className="gacha-card-eggs">
        {Array.from({ length: Math.min(count, 5) }, (_, i) => (
          <span key={i}>🥚</span>
        ))}
        {count > 5 && <span className="gacha-card-more">+{count - 5}</span>}
      </div>
      <h3 className="gacha-card-label">{label}</h3>
      {originalCost && (
        <div className="gacha-card-original">
          {originalCost.toLocaleString()} coins
        </div>
      )}
      <div className="gacha-card-cost">🪙 {cost.toLocaleString()}</div>
      <button
        className={`gacha-card-btn ${!canRoll ? "disabled" : ""} ${premium ? "premium" : featured ? "featured" : ""}`}
        onClick={onRoll}
        disabled={!canRoll}
      >
        {!hasOwls
          ? "No Owls Available"
          : !canAfford
            ? "Not Enough Coins"
            : count === 1
              ? "Roll"
              : `Roll ×${count}`}
      </button>
    </div>
  );
};

const OwlCard = ({ owl, decided, onCollect, onDiscard, compact }) => (
  <div
    className={`gacha-owl-card gacha-owl-${owl.rarity.toLowerCase()} ${compact ? "compact" : ""} reveal-anim`}
  >
    <div
      className={`gacha-rarity-badge gacha-rarity-${owl.rarity.toLowerCase()}`}
    >
      {owl.rarity}
    </div>
    <div className="gacha-owl-img-wrap">
      <div
        className={`gacha-owl-glow gacha-glow-${owl.rarity.toLowerCase()}`}
      />
      <img src={getOwlImage(owl)} alt={owl.name} className="gacha-owl-img" />
    </div>
    <h2 className="gacha-owl-name">{owl.name}</h2>
    {!compact && <p className="gacha-owl-desc">{owl.description}</p>}

    {!decided ? (
      <div className="gacha-card-actions">
        <button className="gacha-btn-collect" onClick={onCollect}>
          ✨ Add to Collection
        </button>
        <button className="gacha-btn-discard" onClick={onDiscard}>
          Discard (+{owl.discard_value} 🪙)
        </button>
      </div>
    ) : (
      <div className={`gacha-decided-badge ${decided}`}>
        {decided === "collected"
          ? "✅ Added to Collection"
          : `🪙 Discarded (+${owl.discard_value})`}
      </div>
    )}
  </div>
);

export default OwlLotterySystem;
